import { Field, Input, Text } from '@fluentui/react-components';
import { getFieldPanelContext } from '@moko/utils';
import { useTranslation } from 'react-i18next';
import { useOtherInfoContext } from '../OtherInfoContext';
import { fieldSizeStyle } from '../panelLayout';
import { isFieldReadOnly } from '../fieldReadOnly';
import { FieldWrapper, ReadOnlyEmpty } from './FieldWrapper';
import {
  isEmptyFieldValue,
  useFieldReadOnly,
  useReadonlyFieldStyles,
} from './fieldReadOnly';
import type { FieldComponentProps } from './types';

const showExtension = (options: unknown): boolean =>
  !!options &&
  typeof options === 'object' &&
  !Array.isArray(options) &&
  (options as { showExtensionInContactField?: unknown })
    .showExtensionInContactField === true;

export const ContactField = ({
  field,
  fieldValue,
  isFieldDisabled,
  isSaved,
  validationMessage,
}: FieldComponentProps) => {
  const { t } = useTranslation();
  const {
    handleOtherInfoChange,
    validateField,
    otherInfoObj,
    getRepeatingPanelIndex,
    currentElementInEntityRepeatingPanel,
    params,
  } = useOtherInfoContext();
  const metadata = params.metadata;

  const withExtension = showExtension(field.options);
  const extFieldName = `${field.name}_Ext`;
  const { fieldParentName, manualRepeating, entityRepeating } =
    getFieldPanelContext(field.name, metadata.fields);

  let extValue: unknown;
  if (fieldParentName && manualRepeating) {
    const index = getRepeatingPanelIndex(fieldParentName);
    extValue = otherInfoObj[fieldParentName]?.[index]?.[extFieldName];
  } else if (fieldParentName && entityRepeating) {
    const entityIndex = otherInfoObj[fieldParentName]?.findIndex(
      (entity: { id?: unknown }) =>
        entity.id === currentElementInEntityRepeatingPanel
    );
    extValue =
      entityIndex >= 0
        ? otherInfoObj[fieldParentName]?.[entityIndex]?.[extFieldName]
        : undefined;
  } else {
    extValue = otherInfoObj[extFieldName];
  }

  const phone = fieldValue == null ? '' : String(fieldValue);
  const ext = extValue == null ? '' : String(extValue);
  const readOnly = isSaved || isFieldReadOnly(field);
  const isViewOnly = useFieldReadOnly(field);
  const readonlyStyles = useReadonlyFieldStyles();
  const labelAbove = Boolean(field.labelAbove);

  const onPhoneChange = (value: string) => {
    handleOtherInfoChange(field.name, value);
    validateField(field.name, value);
  };

  const onExtChange = (value: string) => {
    handleOtherInfoChange(extFieldName, value);
    validateField(extFieldName, value);
  };

  const phoneInput = (
    <Input
      id={field.name}
      type="tel"
      style={{ minWidth: 0, width: '100%' }}
      value={phone}
      onChange={(_, data) => onPhoneChange(data.value)}
      disabled={isFieldDisabled}
      readOnly={readOnly}
      maxLength={field.maxLength}
    />
  );

  const extInput = (
    <Input
      id={extFieldName}
      type="text"
      style={{ minWidth: 0, width: '100%' }}
      value={ext}
      onChange={(_, data) => onExtChange(data.value)}
      disabled={isFieldDisabled}
      readOnly={readOnly}
    />
  );

  if (labelAbove) {
    return (
      <div
        className={isViewOnly ? readonlyStyles.readOnly : undefined}
        style={{
          display: 'grid',
          gridTemplateColumns: withExtension
            ? 'minmax(0, 2fr) minmax(0, 1fr)'
            : 'minmax(0, 1fr)',
          gap: 12,
          alignItems: 'center',
          ...fieldSizeStyle,
        }}
      >
        <Field
          label={t('phone', 'Phone')}
          hint={field.hint}
          required={Boolean(field.mandatory)}
          validationMessage={validationMessage}
        >
          {isViewOnly && isEmptyFieldValue(phone) ? (
            <div className={readonlyStyles.value}>
              <ReadOnlyEmpty />
            </div>
          ) : isViewOnly ? (
            <div className={readonlyStyles.value}>{phoneInput}</div>
          ) : (
            phoneInput
          )}
        </Field>
        {withExtension && (
          <Field label={t('ext', 'Ext')}>
            {isViewOnly && isEmptyFieldValue(ext) ? (
              <div className={readonlyStyles.value}>
                <ReadOnlyEmpty />
              </div>
            ) : isViewOnly ? (
              <div className={readonlyStyles.value}>{extInput}</div>
            ) : (
              extInput
            )}
          </Field>
        )}
      </div>
    );
  }

  return (
    <FieldWrapper field={field} validationMessage={validationMessage}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: withExtension
            ? 'minmax(0, 2fr) auto minmax(0, 1fr)'
            : 'minmax(0, 1fr)',
          gap: 8,
          alignItems: 'center',
          minWidth: 0,
          width: '100%',
        }}
      >
        {isViewOnly && isEmptyFieldValue(phone) ? (
          <ReadOnlyEmpty />
        ) : (
          phoneInput
        )}
        {withExtension && (
          <>
            <Text>{t('ext', 'Ext')}</Text>
            {isViewOnly && isEmptyFieldValue(ext) ? (
              <ReadOnlyEmpty />
            ) : (
              extInput
            )}
          </>
        )}
      </div>
    </FieldWrapper>
  );
};
